import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Chip, CircularProgress, IconButton, TextField } from "@mui/material";
import { IoSend } from "react-icons/io5";
import { SocketContext } from "context/SocketProvider";
import chatsService from "services/admin/chatsService";

interface SandboxChannel {
  id: number;
  source: string;
  externalAccountUserName: string;
}

interface Props {
  sandbox: SandboxChannel;
  // Частые вопросы клиентов — кнопки над полем ввода
  questions: string[];
}

// Сколько ждём ответа агента, прежде чем показать предупреждение
const REPLY_TIMEOUT_MS = 90_000;

/**
 * Тестовый чат с агентом в мастере настройки.
 * Работает через «Тестовую среду» (TEST_SANDBOX), как страница «Тестовая среда»:
 * первое сообщение создаёт чат (chat:initialize), следующие уходят в него (chat:send_message).
 * Новый чат — новый ключ компонента у родителя.
 */
export const AgentSetupChat = ({ sandbox, questions }: Props) => {
  const { send, isConnected } = useContext<any>(SocketContext);
  const orgID = localStorage.getItem("orgID") as string;

  // Идентификатор тестового клиента — свой на каждую сессию, чтобы старая переписка не влияла
  const [externalChatID, setExternalChatID] = useState<string | null>(null);
  // Последнее отправленное сообщение: ждём на него ответ
  const [sent, setSent] = useState<{ text: string; baseCount: number } | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [text, setText] = useState("");
  const bodyRef = useRef<HTMLDivElement>(null);

  // Чат появляется на сервере не сразу — ищем его, пока не найдём
  const { data: chatID } = useQuery({
    queryKey: ["agent-setup-chat", externalChatID],
    enabled: !!externalChatID,
    queryFn: async () => {
      const { data } = await chatsService.getChats({
        orgID,
        source: sandbox.source,
        externalAccountUserName: sandbox.externalAccountUserName,
      });
      const chat = (data?.rows || []).find((c: any) => c.externalChatID === externalChatID);
      return (chat?.id as number) ?? null;
    },
    refetchInterval: (query) => (query.state.data ? false : 2000),
  });

  // Тот же ключ, что у useGetChatMessages: сокет дописывает сюда новые сообщения.
  // Пока ждём ответ, дополнительно опрашиваем сервер — на случай, если сокет отвалился
  const waiting = !!sent && !timedOut;
  const { data: rawMessages } = useQuery({
    queryKey: ["chat", chatID],
    enabled: !!chatID,
    queryFn: async () => {
      const response = await chatsService.getMessagesByChatID(chatID as number, {});
      return response.data?.rows || [];
    },
    refetchInterval: waiting ? 3000 : false,
    refetchOnWindowFocus: false,
  });

  // Сокет и опрос могут прислать одно сообщение дважды
  const messages: any[] = useMemo(() => {
    const seen = new Set();
    return (rawMessages || []).filter((m: any) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [rawMessages]);

  const newMessages = sent ? messages.slice(sent.baseCount) : [];
  const userArrived = newMessages.some((m) => m.senderType === "user");
  const replied = newMessages.some((m) => m.senderType !== "user");

  useEffect(() => {
    if (sent && replied) {
      setSent(null);
      setTimedOut(false);
    }
  }, [sent, replied]);

  useEffect(() => {
    if (!sent) return;
    const timer = setTimeout(() => setTimedOut(true), REPLY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [sent]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, sent]);

  const canSend = isConnected && !waiting && (!externalChatID || !!chatID);

  const sendText = (value: string) => {
    const message = value.trim();
    if (!message || !canSend) return;

    if (!externalChatID) {
      const id = `test-${Date.now().toString(36)}`;
      setExternalChatID(id);
      send({ event: "chat:initialize", data: { text: message, chatID: id, channelConnectionID: sandbox.id } });
    } else {
      send({ event: "chat:send_message", data: { chatID, text: message } });
    }
    setSent({ text: message, baseCount: messages.length });
    setTimedOut(false);
    setText("");
  };

  // Наше сообщение показываем сразу, не дожидаясь сервера
  const visible = [...messages];
  if (sent && !userArrived) {
    visible.push({ id: "pending", senderType: "user", text: sent.text });
  }

  return (
    <div className="flex flex-col h-full border rounded-2xl overflow-hidden bg-white">
      <div ref={bodyRef} className="flex-grow overflow-y-auto p-4 flex flex-col gap-3 bg-gray-50">
        {visible.length === 0 && (
          <div className="m-auto text-center text-gray-500 max-w-sm">
            Задайте вопрос так, как спросил бы клиент, или выберите частый вопрос ниже.
          </div>
        )}
        {visible.map((m) => {
          const isClient = m.senderType === "user";
          return (
            <div key={m.id} className={`flex ${isClient ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 whitespace-pre-wrap break-words ${
                  isClient ? "bg-[#6b2fa3] text-white rounded-br-sm" : "bg-white border rounded-bl-sm"
                }`}
              >
                {!isClient && <div className="text-xs text-gray-400 mb-1">ИИ-агент</div>}
                {m.text || <span className="italic opacity-70">Сообщение без текста</span>}
              </div>
            </div>
          );
        })}
        {waiting && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CircularProgress size={14} />
            Агент печатает… обычно несколько секунд
          </div>
        )}
        {timedOut && (
          <Alert severity="warning">
            Агент пока не ответил. Подождите ещё немного или начните новый чат. Если агент молчит всегда — проверьте,
            что он включён.
          </Alert>
        )}
      </div>

      <div className="border-t p-3 flex flex-col gap-3">
        {!isConnected && (
          <Alert severity="warning" className="!py-0">
            Нет соединения с сервером. Переподключаемся…
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          {questions.map((q) => (
            <Chip key={q} label={q} variant="outlined" clickable disabled={!canSend} onClick={() => sendText(q)} />
          ))}
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            sendText(text);
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Напишите свой вопрос…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <IconButton type="submit" color="primary" disabled={!canSend || !text.trim()} aria-label="Отправить">
            <IoSend />
          </IconButton>
        </form>
      </div>
    </div>
  );
};

export default AgentSetupChat;
